"use client";

import { useRef } from "react";
import GridClearing from "@/components/GridClearing";
import { GradText, SectionHead, SectionDecorations } from "./kit";
import type { DecoItem } from "./kit/SectionDecorations";
import { useEnterStagger } from "./kit/motion";
import WorkflowDemo from "./WorkflowDemo";

/** Routing-themed margin decorations for this section's field. */
const ROUTE_DECOS: DecoItem[] = [
  { type: "label", text: "IF · ELSE", position: "top" },
  { type: "label", text: "GEO", position: "bottom" },
  { type: "label", text: "DEVICE", position: "top" },
  { type: "label", text: "30X", position: "bottom" },
  { type: "star", size: 34 },
  { type: "star", size: 30 },
  { type: "panel", label: "[1×1]" },
  { type: "cube", variant: "b" },
];

export default function SmartRoutingSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  // The workflow window rises as the section enters (SectionHead choreographs
  // itself). Animate the inner wrapper, never the [data-grid-block] itself —
  // GridClearing measures the block with getBoundingClientRect().
  useEnterStagger(rootRef, "[data-workflow-inner]", { y: 36, duration: 1 });

  return (
    <section id="ref-routing" className="relative py-[118px]">
      <SectionDecorations inventory={ROUTE_DECOS} density={4} />
      {/* Mobile: the standard centered container. md+: the content column is the
          grid container narrowed by one cell (so it sits ½ a cell inside the grid
          line on each side) — the heading and workflow window start mid-column
          rather than pinned to a vertical grid line, the horizontal analogue of
          vertically centring content in a row, while keeping a comfortable side
          margin on narrower windows. GridClearing snaps to whole cells (never a
          partial line) and removes the bordering vertical lines so content flows
          freely on x; horizontal row lines stay. */}
      <div
        ref={rootRef}
        className="relative mx-auto w-full max-w-[1200px] px-7 md:max-w-full md:px-0 md:w-[calc(var(--grid-container-width,1224px)_-_var(--grid-cell,72px))]"
      >
        <GridClearing rootRef={rootRef} />

        <div className="relative z-10">
          {/* Heading sits in its own cleared block (left-aligned column) —
              SectionHead marks itself as a data-grid-block. */}
          <SectionHead
            eyebrow="Smart routing"
            title={
              <>
                One link, the <GradText>right destination</GradText> for every visitor.
              </>
            }
            lead="Drag conditions onto a canvas to route by device, geo or time — each visitor gets the response that fits: a redirect to the App Store, a localized page, or custom content inline. Routing rules come with Premium, and every visit resolves as it arrives."
          />

          <div data-grid-block>
            <div data-workflow-inner>
              <WorkflowDemo />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
